import { type CSSProperties, useMemo, useState } from 'react';

import { getNameInitials, mergeClassNames } from '@moniebook-web/utils';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';

import { UserDropdown } from '../user-dropdown';
import type { SidebarMenuGroup } from './types';

interface IconRailProps {
    groups: SidebarMenuGroup[];
    activeGroupId: string | null;
    onGroupSelect: (id: string) => void;
    businessName: string;
}

export function IconRail({
    groups,
    activeGroupId,
    onGroupSelect,
    businessName,
}: Readonly<IconRailProps>) {
    const [expanded, setExpanded] = useState(false);
    const handleGroupSelect = (id: string) => {
        onGroupSelect(id);
        setExpanded(false);
    };

    const activeGroup = useMemo(
        () => groups.find((group) => group.id === activeGroupId) || null,
        [groups, activeGroupId],
    );

    const computedExpanded = expanded || !activeGroup?.children.length;

    const style = {
        width: computedExpanded ? 250 : 48.5,
        borderRightWidth: computedExpanded ? 1 : 0,
        boxShadow: expanded ? '1px 0 2px 0 rgba(0, 0, 0, 0.04)' : 'none',
    };

    return (
        <motion.div
            initial={style}
            animate={style}
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
            transition={{
                duration: 0.3,
                ease: [0.87, 0, 0.13, 1],
            }}
            className={mergeClassNames(
                'relative z-10 flex h-full flex-col overflow-auto overscroll-none border-ui-border-default bg-white',
                'after:absolute after:top-0 after:bottom-0 after:left-48 after:z-[11] after:w-[0.5px] after:bg-ui-border-default',
            )}>
            <RailHeader businessName={businessName} />
            {groups.map((group) => (
                <RailItem
                    key={group.id}
                    group={group}
                    isActive={activeGroupId === group.id}
                    onGroupSelect={handleGroupSelect}
                    onMouseEnter={() => setExpanded(true)}
                />
            ))}
        </motion.div>
    );
}

interface RailHeaderProps {
    businessName: string;
}

function RailHeader({ businessName }: Readonly<RailHeaderProps>) {
    return (
        <div className='sticky top-0 z-10 flex h-48 w-full shrink-0 bg-white'>
            <div className='sticky top-0 flex h-full w-48 shrink-0 items-center justify-center'>
                <div className='flex size-32 min-w-0 select-none items-center justify-center truncate rounded-full bg-content-heading-default font-medium text-11 text-white'>
                    {getNameInitials(businessName)}
                </div>
            </div>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: onMouseEnter only stops event propagation; there's no user action here for a keyboard equivalent to trigger */}
            <div
                onMouseEnter={(e) => e.stopPropagation()}
                className='flex h-full min-w-0 items-center px-12'>
                <UserDropdown />
            </div>
        </div>
    );
}

interface IconRailItemProps {
    group: SidebarMenuGroup;
    isActive: boolean;
    onGroupSelect: (id: string) => void;
    bottom?: boolean;
    onMouseEnter?: () => void;
}

function RailItem({
    group,
    isActive,
    onGroupSelect,
    onMouseEnter,
}: Readonly<IconRailItemProps>) {
    const link = group.to || group.children?.[0]?.to;
    const navigate = useNavigate();
    const handleClick = () => {
        onGroupSelect(group.id);
        if (link) navigate(link);
    };

    return (
        <button
            type='button'
            className={clsx(
                'group flex h-48 w-full shrink-0 items-center outline-none hover:bg-[--bg-color]',
            )}
            onClick={handleClick}
            aria-label={group.name}
            style={
                {
                    '--color': group.foregroundColor,
                    '--bg-color': group.backgroundColor,
                } as CSSProperties
            }
            onMouseEnter={onMouseEnter}>
            <div
                className={mergeClassNames(
                    'relative flex w-48 shrink-0 items-center justify-center',
                    isActive &&
                        'after:absolute after:top-1/2 after:right-0 after:h-12 after:w-2 after:-translate-y-1/2 after:bg-[--color]',
                )}>
                <div
                    className={clsx(
                        'flex items-center justify-center',
                        'size-32 rounded-[8px] bg-[--bg-color]',
                        'group-hover:ring-2 group-hover:ring-[--color] group-hover:ring-offset-2',
                        'group-focus:ring-2 group-focus:ring-[--color] group-focus:ring-offset-2',
                        isActive &&
                            'bg-none ring-2 ring-[--color] ring-offset-2',
                    )}>
                    {group.icon}
                </div>
            </div>
            <div className='flex items-center whitespace-nowrap px-12 font-normal text-14 text-new-neutral-1000'>
                {group.name}
            </div>
        </button>
    );
}
